import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { traceService } from '../services/traceService'

export type LongConversationHealth = 'healthy' | 'warning' | 'degraded'

export type LongConversationRow = {
  tenantId: string
  conversationId: string
  turns: number
  currentStage?: string | null
  stagnationTurns?: number | null
  lastActivityAt?: string | null

  health?: LongConversationHealth | string

  checkpointSizeBytes?: number | null
  summaryCount?: number | null
  strategicSnapshotCount?: number | null
  memoryPruningEvents?: number | null
  repeatedNodePattern?: string | null
  noProgressTurns?: number | null

  funnelAuthorityMode?: string | null
  routerSuggestedRoute?: string | null
  deciderSuggestedStage?: string | null
  finalEffectiveRoute?: string | null
  overrideApplied?: boolean | null
  overrideBlockedReason?: string | null
}

function healthPill(health: string) {
  if (health === 'critical' || health === 'degraded') {
    return { background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.35)', color: '#fecaca' }
  }
  if (health === 'warning') {
    return { background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.32)', color: '#fde68a' }
  }
  return { background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.30)', color: '#bbf7d0' }
}

function when(value: string | null | undefined) {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export function LongConversationsView({
  tenantId,
  rows,
  loading,
  error,
  onRefresh,
}: {
  tenantId: string
  rows: LongConversationRow[]
  loading: boolean
  error: string
  onRefresh: () => void
}) {
  const emptyLabel = !tenantId
    ? 'Selecciona un tenant para ver conversaciones largas.'
    : loading
      ? 'Cargando conversaciones...'
      : error
        ? error
        : 'No hay conversaciones largas en esta ventana.'

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>Conversaciones Largas</div>
          <div style={{ fontSize: 12, color: 'rgba(226,232,240,0.65)' }}>
            Visibilidad de degradacion: turns, checkpoint size, stagnation, patrones repetidos y autoridad del funnel.
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={!tenantId || loading}
          style={{
            padding: '8px 10px',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'rgba(255,255,255,0.04)',
            color: '#e2e8f0',
            fontSize: 12,
            fontWeight: 800,
            cursor: !tenantId || loading ? 'not-allowed' : 'pointer',
            opacity: !tenantId || loading ? 0.6 : 1,
          }}
        >
          Refresh
        </button>
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: 14, borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.03)' }}>
          {emptyLabel}
        </div>
      ) : (
        <div style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, overflow: 'hidden', background: 'rgba(255,255,255,0.02)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '96px 120px 110px 1fr 220px 160px', gap: 0, padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: 11, color: 'rgba(226,232,240,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            <div>Health</div>
            <div>Turns</div>
            <div>Stage</div>
            <div>Funnel authority</div>
            <div>Last activity</div>
            <div>Accion</div>
          </div>

          {rows.map((row) => {
            const health = String(row.health || 'healthy')
            const toDebug = `/admin/debug?tenantId=${encodeURIComponent(row.tenantId)}&conversationId=${encodeURIComponent(row.conversationId)}`
            return (
              <div key={row.conversationId} style={{ display: 'grid', gridTemplateColumns: '96px 120px 110px 1fr 220px 160px', gap: 0, padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', alignItems: 'start' }}>
                <div>
                  <span style={{ display: 'inline-flex', padding: '4px 8px', borderRadius: 999, fontSize: 11, fontWeight: 900, ...healthPill(health) }}>
                    {health}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'rgba(226,232,240,0.85)' }}>
                  <div style={{ fontWeight: 900 }}>{row.turns}</div>
                  <div style={{ fontSize: 11, color: 'rgba(226,232,240,0.55)' }}>stagnation: {row.stagnationTurns ?? '-'}</div>
                  <div style={{ fontSize: 11, color: 'rgba(226,232,240,0.55)' }}>cp size: {row.checkpointSizeBytes ?? '-'}</div>
                </div>
                <div style={{ fontSize: 12, color: 'rgba(226,232,240,0.85)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>
                  {row.currentStage || '-'}
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.45 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', padding: '4px 8px', borderRadius: 999, fontSize: 11, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.03)', color: 'rgba(226,232,240,0.8)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>
                      mode={row.funnelAuthorityMode ?? '-'}
                    </span>
                    <span style={{ display: 'inline-flex', padding: '4px 8px', borderRadius: 999, fontSize: 11, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.03)', color: 'rgba(226,232,240,0.8)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>
                      final={row.finalEffectiveRoute ?? '-'}
                    </span>
                    <span style={{ display: 'inline-flex', padding: '4px 8px', borderRadius: 999, fontSize: 11, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.03)', color: 'rgba(226,232,240,0.8)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>
                      override={row.overrideApplied ? 'true' : 'false'}
                    </span>
                  </div>
                  <div style={{ marginTop: 6, color: 'rgba(226,232,240,0.68)' }}>
                    router={row.routerSuggestedRoute ?? '-'} | deciderStage={row.deciderSuggestedStage ?? '-'}
                  </div>
                  {row.overrideBlockedReason ? (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#fde68a' }}>
                      blocked: {row.overrideBlockedReason}
                    </div>
                  ) : null}
                  {row.repeatedNodePattern ? (
                    <div style={{ marginTop: 6, fontSize: 11, color: 'rgba(226,232,240,0.55)' }}>
                      pattern: {row.repeatedNodePattern}
                    </div>
                  ) : null}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(226,232,240,0.75)' }}>{when(row.lastActivityAt)}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Link className="obs-link" to={toDebug}>Abrir trace</Link>
                  <details>
                    <summary style={{ fontSize: 11, color: 'rgba(226,232,240,0.65)', cursor: 'pointer' }}>Detalles</summary>
                    <pre style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap', fontSize: 11, color: '#a7f3d0', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.18)', borderRadius: 12, padding: 12 }}>
{JSON.stringify(row, null, 2)}
                    </pre>
                  </details>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function DebugObservabilityConversations({ tenantId }: { tenantId: string }) {
  const [rows, setRows] = useState<LongConversationRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const refresh = async () => {
    if (!tenantId) return
    setLoading(true)
    setError('')
    try {
      const res = await traceService.getObservabilityConversations({ tenantId, limit: 50 })
      const mapped = (res.data ?? []).map((raw: any) => ({
        tenantId: String(raw.tenantId ?? tenantId),
        conversationId: String(raw.conversationId ?? ''),
        turns: Number(raw.turns ?? 0),
        currentStage: raw.currentStage ?? null,
        stagnationTurns: raw.stagnationTurns ?? null,
        lastActivityAt: raw.lastActivity ?? raw.lastActivityAt ?? null,
        health: raw.healthStatus ?? raw.health ?? 'healthy',
        checkpointSizeBytes: raw.checkpointSizeBytes ?? null,
        summaryCount: raw.summaryCount ?? null,
        strategicSnapshotCount: raw.strategicSnapshotCount ?? null,
        memoryPruningEvents: raw.memoryPruningEvents ?? null,
        repeatedNodePattern: raw.repeatedNodePattern ?? null,
        noProgressTurns: raw.noProgressTurns ?? null,
        funnelAuthorityMode: raw.funnelAuthorityMode ?? null,
        routerSuggestedRoute: raw.routerSuggestedRoute ?? null,
        deciderSuggestedStage: raw.deciderSuggestedStage ?? null,
        finalEffectiveRoute: raw.finalEffectiveRoute ?? null,
        overrideApplied: raw.overrideApplied ?? null,
        overrideBlockedReason: raw.overrideBlockedReason ?? null,
      })) as LongConversationRow[]

      setRows(mapped.filter((row) => row.conversationId))
    } catch {
      setRows([])
      setError('No se pudieron cargar las conversaciones largas')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [tenantId])

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const ah = String(a.health || 'healthy')
      const bh = String(b.health || 'healthy')
      const score = (h: string) => h === 'degraded' ? 3 : h === 'warning' ? 2 : 1
      const s = score(bh) - score(ah)
      if (s !== 0) return s
      return (b.turns || 0) - (a.turns || 0)
    })
  }, [rows])

  return (
    <LongConversationsView
      tenantId={tenantId}
      rows={sorted}
      loading={loading}
      error={error}
      onRefresh={refresh}
    />
  )
}
